import type { IsoProjection } from '../core/iso';

/** Axis-aligned rectangle in world space. */
export interface WorldRect {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

/**
 * Splits the map into square chunks of tiles and answers "which chunks touch
 * this world rectangle?". Pure and engine-free.
 *
 * Phaser does not cull ordinary sprites, so without this every ground tile,
 * overlay tile and tree is processed every frame even when off-screen.
 */
export class ChunkGrid {
  readonly chunksX: number;
  readonly chunksY: number;
  /** World AABB per chunk, indexed cy * chunksX + cx. */
  private readonly bounds: WorldRect[] = [];

  constructor(
    readonly iso: IsoProjection,
    readonly mapWidth: number,
    readonly mapHeight: number,
    readonly chunkSize: number,
  ) {
    if (!Number.isInteger(chunkSize) || chunkSize < 1) throw new Error(`Invalid chunk size ${chunkSize}`);
    this.chunksX = Math.ceil(mapWidth / chunkSize);
    this.chunksY = Math.ceil(mapHeight / chunkSize);
    for (let cy = 0; cy < this.chunksY; cy++) {
      for (let cx = 0; cx < this.chunksX; cx++) {
        const x0 = cx * chunkSize;
        const y0 = cy * chunkSize;
        const x1 = Math.min(mapWidth, x0 + chunkSize) - 1;
        const y1 = Math.min(mapHeight, y0 + chunkSize) - 1;
        this.bounds.push({
          left: iso.gridToWorld(x0, y1).x - iso.halfWidth,
          right: iso.gridToWorld(x1, y0).x + iso.halfWidth,
          top: iso.gridToWorld(x0, y0).y - iso.halfHeight,
          bottom: iso.gridToWorld(x1, y1).y + iso.halfHeight,
        });
      }
    }
  }

  get count(): number {
    return this.bounds.length;
  }

  /** Chunk index containing a tile. */
  chunkOf(x: number, y: number): number {
    return Math.floor(y / this.chunkSize) * this.chunksX + Math.floor(x / this.chunkSize);
  }

  chunkBounds(i: number): WorldRect {
    const b = this.bounds[i];
    if (!b) throw new RangeError(`Chunk ${i} out of range`);
    return b;
  }

  /**
   * Chunks whose bounds, grown by `margin` on every side and by `extraTop`
   * more on the TOP side, intersect the view. `extraTop` covers objects that
   * are rooted in the chunk but rise above it on screen (trees, later walls
   * and towers), so they don't pop out while still partly visible.
   */
  visible(view: WorldRect, margin: number, extraTop = 0): Set<number> {
    const out = new Set<number>();
    for (let i = 0; i < this.bounds.length; i++) {
      const b = this.bounds[i]!;
      if (b.right + margin < view.left || b.left - margin > view.right) continue;
      if (b.bottom + margin < view.top || b.top - margin - extraTop > view.bottom) continue;
      out.add(i);
    }
    return out;
  }
}
